import { ReactNode, useEffect, useRef, useState } from 'react';
import styles from './Revelar.module.css';

type Props = {
  children: ReactNode;
  /** Atraso em ms para escalonar a entrada (stagger). */
  atraso?: number;
  className?: string;
};

/**
 * Scroll reveal: renderiza o conteúdo invisível (opacity 0 + translateY) e
 * anima a entrada quando ~15% do elemento entra no viewport — uma única vez.
 * `prefers-reduced-motion` é respeitado no CSS (renderiza visível direto).
 */
export default function Revelar({ children, atraso = 0, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visivel, setVisivel] = useState(false);

  useEffect(() => {
    const elemento = ref.current;
    if (!elemento) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisivel(true);
      return;
    }

    const observador = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((entrada) => {
          if (entrada.isIntersecting) {
            setVisivel(true);
            observador.unobserve(entrada.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    observador.observe(elemento);
    return () => observador.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${styles.revelar} ${visivel ? styles.visivel : ''} ${className ?? ''}`}
      style={atraso ? { transitionDelay: `${atraso}ms` } : undefined}
    >
      {children}
    </div>
  );
}
